import { LeaseStatus } from '@zg-rentals/ts-rental-manager-api-client';
import type Lease from '../../models/Lease';
import { LeaseTypes } from '../../models/Types';
import type LeaseExtension from '../../models/LeaseExtension';

export const getLeaseStatusDescription = (lease: Lease) => {
  const map = {
    [LeaseStatus.ACTIVE]: (_: Lease) => `Your lease is active`,
    [LeaseStatus.COUNTERSIGNING]: (_: Lease) =>
      `Waiting for signatures. Your lease will be ratified once everyone signs.`,
    [LeaseStatus.EXPIRED]: (lease: Lease) => {
      return lease.getFriendlyEndDate() ? `This lease expired on ${lease.getFriendlyEndDate()}` : `This lease expired`;
    },
    [LeaseStatus.SENT_COMPLETE_DRAFT]: (_: Lease) =>
      `Your landlord is updating this lease, but they haven’t shared a new version yet`,
    [LeaseStatus.SENT_FOR_ONLINE_SIGNING]: (_: Lease) => `Your lease is ready to sign`,
    [LeaseStatus.SENT_FOR_OFFLINE_SIGNING]: (_: Lease) => `Your lease is ready to sign`,
    [LeaseStatus.SENT_INCOMPLETE_DRAFT]: (_: Lease) =>
      `Your landlord is updating this lease, but they haven‘t shared a new version yet`,
    [LeaseStatus.SIGNED_BY_TENANT]: (_: Lease) =>
      `Waiting for signatures. Your lease will be ratified once everyone signs.`,
    [LeaseStatus.SIGNING_CANCELLED]: (_: Lease) =>
      `Signing has been canceled for now. Any signatures have been removed, but you can still view the lease.`,
    [LeaseStatus.SIGNING_COMPLETE]: (lease: Lease) => {
      return lease.leaseType === LeaseTypes.SIGNED_SELF_UPLOAD
        ? `Your lease is active on ${lease.getFriendlyStartDate()}. You can always come back and view the lease here, and ask questions to your landlord.`
        : `Everyone has signed. Your lease becomes active on ${lease.getFriendlyStartDate()} if any deposits are paid.`;
    },
    [LeaseStatus.TERMINATED]: (_: Lease) => `Your lease has been canceled`,
  };

  if (lease == null || map[lease.status] == null) {
    return lease ? lease.status : null;
  }

  return map[lease.status](lease);
};

export const getLeaseRenewalStatusDescription = (leaseExtension: LeaseExtension) => {
  const map = {
    [LeaseStatus.ACTIVE]: (_: LeaseExtension) => `Your lease renewal agreement is active`,
    [LeaseStatus.COUNTERSIGNING]: (_: LeaseExtension) =>
      `Waiting for signatures. Your lease renewal agreement will be ratified once everyone signs.`,
    [LeaseStatus.EXPIRED]: (leaseExtension: LeaseExtension) => {
      return leaseExtension.getFriendlyEndDate()
        ? `This lease renewal agreement expired on ${leaseExtension.getFriendlyEndDate()}`
        : `This lease renewal agreement expired`;
    },
    [LeaseStatus.SENT_COMPLETE_DRAFT]: (_: LeaseExtension) =>
      `Your landlord is updating this lease renewal agreement, but they haven’t shared a new version yet`,
    [LeaseStatus.SENT_FOR_ONLINE_SIGNING]: (_: LeaseExtension) => `Your lease renewal agreement is ready to sign`,
    [LeaseStatus.SENT_INCOMPLETE_DRAFT]: (_: LeaseExtension) =>
      `Your landlord is updating this lease renewal agreement, but they haven‘t shared a new version yet`,
    [LeaseStatus.SIGNED_BY_TENANT]: (_: LeaseExtension) =>
      `Waiting for signatures. Your lease renewal agreement will be ratified once everyone signs.`,
    [LeaseStatus.SIGNING_CANCELLED]: (_: LeaseExtension) =>
      `Signing has been canceled for now. Any signatures have been removed, but you can still view the lease renewal agreement.`,
    [LeaseStatus.SIGNING_COMPLETE]: (leaseExtension: LeaseExtension) => {
      return `Everyone has signed. Your lease renewal agreement becomes active on ${leaseExtension.getFriendlyStartDate()}.  Until then, refer to your original lease.`;
    },
  };

  if (leaseExtension == null || map[leaseExtension.status] == null) {
    return leaseExtension ? leaseExtension.status : null;
  }

  return map[leaseExtension.status](leaseExtension);
};

export const LEASE_SUMMARY_CHECKBOX_LABELS = {
  ONLINE_SIGNING: {
    WITH_DOCUMENTS: `
        I understand and agree that Zillow is not responsible for rules included in the rules addendum, if any.
    `,
    WITH_NO_DOCUMENTS: `
        I understand and agree that Zillow is not responsible for rules included in the rules addendum, if any.
    `,
  },
  OFFLINE_SIGNING: {
    WITH_DOCUMENTS: `
      I understand and agree that Zillow is not responsible for rules included in the rules addendum, if any,
      nor ensuring that any version of a lease viewed offline or otherwise outside of our system is the same as
      the one you reviewed in the system. I will ensure that the landlord provides me a copy of the lease I signed
      offline, including all the required signatures.
    `,
    WITH_NO_DOCUMENTS: `
      I understand and agree that Zillow is not responsible for rules included in the rules addendum, if any, nor
      ensuring that any version of a lease viewed offline or otherwise outside of our system is the same as the one
      you reviewed in the system. I will ensure that the landlord provides me a signed copy of the lease I signed
      offline.
    `,
  },
  SELF_SERVE: {
    WITH_NO_DOCUMENTS: `
      I understand and agree that my landlord created this lease, and Zillow is not responsible for its content, enforceability, or compliance with applicable law.
    `,
  },
};
